"use client";

import React, { useRef, useState } from "react";
import {
  AgentActionPayload,
  AgentMessage,
  PresetOption,
} from "@/types/createAgent";
import { logger } from "@/utils/logger";
import { REFERENCE_IMAGE_FORMAT_LABEL } from "@/lib/create/referenceImageUpload";
import {
  describeReferenceImageSelection,
  prepareReferenceImages,
  uploadPreparedReferenceImages,
} from "@/lib/create/referenceImageUploadClient";
import { useCreatePageStore } from "@/stores/createPageStore";
import { useAuth } from "@/context/providers/AuthProvider";
import { useToast } from "@/context/ToastContext";
import { buildActionPayload } from "../createAgentChat.utils";
import type { ChatActions } from "../ChatActionsProvider";

/** Which side an upload preset targets; back only when it says so. */
function uploadPresetSide(preset: PresetOption): "front" | "back" {
  const suggestedField = preset.actionData?.suggestedField;
  const data = (preset.data ?? {}) as Record<string, unknown>;
  return suggestedField === "back_upload" || data.isBackUploadPreset === true
    ? "back"
    : "front";
}

interface UseChatUploadParams {
  sendAgentMessage: ChatActions["sendAgentMessage"];
  addMessage: (message: Omit<AgentMessage, "id" | "timestamp">) => void;
  /** Marks the preset as selected on its message while the picker is open. */
  setSelectedPresetLabel: (messageId: string, label: string) => void;
  /** Clears the selection when the upload is cancelled or fails. */
  clearSelectedPresetLabel: (messageId: string) => void;
}

/**
 * Hidden-input upload flow for upload-type presets: remembers which preset
 * (and message) triggered the picker, reads the chosen files as data URLs,
 * and sends them as an agent message with the preset's action payload.
 */
export function useChatUpload({
  sendAgentMessage,
  addMessage,
  setSelectedPresetLabel,
  clearSelectedPresetLabel,
}: UseChatUploadParams) {
  const { user } = useAuth();
  const { showToast } = useToast();
  const setUploadedFrontImages = useCreatePageStore(
    state => state.setUploadedFrontImages
  );
  const setUploadedBackImages = useCreatePageStore(
    state => state.setUploadedBackImages
  );
  const uploadInputRef = useRef<HTMLInputElement>(null);
  const pendingUploadMessageIdRef = useRef<string | null>(null);
  const pendingUploadActionSourceRef = useRef<
    AgentActionPayload["source"] | undefined
  >(undefined);
  const [pendingUploadPreset, setPendingUploadPreset] =
    useState<PresetOption | null>(null);

  const startPresetUpload = (
    messageId: string,
    preset: PresetOption,
    source?: AgentActionPayload["source"]
  ) => {
    setPendingUploadPreset(preset);
    pendingUploadMessageIdRef.current = messageId;
    pendingUploadActionSourceRef.current = source;
    // Selection is only marked once files are actually chosen — marking here
    // would leave a false "Selected: Upload..." receipt when the user cancels
    // the OS picker (which fires no change event).
    window.setTimeout(() => uploadInputRef.current?.click(), 0);
  };

  // Browsers fire `cancel` (not `change`) when the file dialog is dismissed —
  // clear the pending upload so the widget does not stay armed forever.
  React.useEffect(() => {
    const input = uploadInputRef.current;
    if (!input) return;
    const handleCancel = () => {
      setPendingUploadPreset(null);
      const pendingMessageId = pendingUploadMessageIdRef.current;
      pendingUploadMessageIdRef.current = null;
      pendingUploadActionSourceRef.current = undefined;
      if (pendingMessageId) {
        clearSelectedPresetLabel(pendingMessageId);
      }
    };
    input.addEventListener("cancel", handleCancel);
    return () => input.removeEventListener("cancel", handleCancel);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [uploadInputRef.current]);

  const handleChatUploadImages = async (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const uploadPreset = pendingUploadPreset;
    const pendingMessageId = pendingUploadMessageIdRef.current;
    const uploadActionSource = pendingUploadActionSourceRef.current;
    setPendingUploadPreset(null);
    pendingUploadMessageIdRef.current = null;
    pendingUploadActionSourceRef.current = undefined;
    const files = Array.from(event.target.files ?? []);
    event.target.value = "";

    if (!uploadPreset || files.length === 0) {
      if (pendingMessageId) {
        clearSelectedPresetLabel(pendingMessageId);
      }
      return;
    }

    try {
      // The API rejects data URLs over ~750K chars (~550KB binary) and the
      // vision reader caps at 500KB — a typical phone photo is 2-5MB, so
      // uncompressed uploads would 413 after the agent just invited them.
      // Downscale and normalize every accepted format to JPEG. Preserving PNG
      // here made the quality setting a no-op, so a valid 2-5MB PNG could pass
      // the picker limit and then fail the internal upload limit.
      const { entries: usableEntries, rejectedCount } =
        await prepareReferenceImages(files);
      if (usableEntries.length === 0) {
        if (pendingMessageId) {
          clearSelectedPresetLabel(pendingMessageId);
        }
        addMessage({
          role: "assistant",
          content: `That upload did not include a usable image. Choose ${REFERENCE_IMAGE_FORMAT_LABEL} up to 5 MB, or try a smaller screenshot.`,
        });
        return;
      }

      const partialSelectionNotice = describeReferenceImageSelection(
        usableEntries.length,
        rejectedCount
      );
      if (partialSelectionNotice) {
        showToast(partialSelectionNotice, "warning");
      }

      if (pendingMessageId) {
        setSelectedPresetLabel(pendingMessageId, uploadPreset.label);
      }

      const side = uploadPresetSide(uploadPreset);
      // The generation call reads reference PIXELS from form state
      // (uploadedFrontImages/uploadedBackImages) — the chat turn alone never
      // reached it, so uploaded inspiration influenced only the prompt text
      // (July session audit). Wire the actual files in so the render sees them,
      // and so front-design readiness counts the upload as visual direction.
      const usableFiles = usableEntries
        .map(entry => entry.file)
        .filter((file): file is File => file instanceof File);
      if (usableFiles.length > 0) {
        if (side === "back") {
          setUploadedBackImages(usableFiles);
        } else {
          setUploadedFrontImages(usableFiles);
        }
      }

      // Signed-in uploads go to storage so chat, history, and the design brief
      // carry small hosted URLs; anonymous (or failed) uploads fall back to
      // inline data URLs, which everything already tolerates.
      const dataUrls = usableEntries.map(entry => entry.dataUrl);
      const hostedUrls = user
        ? await uploadPreparedReferenceImages(dataUrls, side)
        : null;
      const imageUrls = hostedUrls ?? dataUrls;
      const displayMessage =
        partialSelectionNotice ??
        (imageUrls.length === 1
          ? "Reference image added"
          : `${imageUrls.length} reference images added`);
      await sendAgentMessage("Uploaded an image.", imageUrls[0], imageUrls, {
        displayMessage,
        hideUserMessage: false,
        actionPayload: buildActionPayload(uploadPreset, uploadActionSource),
      });
    } catch (error) {
      if (pendingMessageId) {
        clearSelectedPresetLabel(pendingMessageId);
      }
      logger.error("Failed to read chat upload images", {
        component: "CreateAgentChat",
        metadata: {
          error: error instanceof Error ? error.message : String(error),
          fileCount: files.length,
        },
      });
      addMessage({
        role: "assistant",
        content: "We couldn't read that image. Please try another file.",
      });
    }
  };

  return {
    uploadInputRef,
    startPresetUpload,
    handleChatUploadImages,
  };
}
